/**
 * 上传服务
 * 处理文件上传到 OSS 的业务逻辑
 */

import type { HttpClient } from '../http/HttpClient'
import type { IStorage } from '../storage/Storage'
import type {
  UploadParams,
  UploadResult,
  UploadLimits,
  SupportedFileType
} from '../../types/upload'
import { DEFAULT_UPLOAD_LIMITS } from '../../types/upload'
import { UploadAPI } from '../api/upload'
import { BaseService } from './BaseService'

export class UploadService extends BaseService {
  private uploadAPI: UploadAPI
  private limits: UploadLimits

  constructor(httpClient: HttpClient, storage: IStorage, limits?: Partial<UploadLimits>) {
    super(httpClient, storage)
    this.uploadAPI = new UploadAPI(httpClient)
    this.limits = { ...DEFAULT_UPLOAD_LIMITS, ...limits }
  }

  /**
   * 上传文件到 OSS
   */
  async upload(params: UploadParams): Promise<UploadResult> {
    const { file, scene = 'image', keyType = 2, onProgress } = params

    try {
      // 1. 验证文件
      await this.validateFile(file)

      // 2. 获取上传凭证
      const tokenResponse = await this.uploadAPI.getUploadToken(scene, keyType)
      const config = tokenResponse.data.config

      // 3. 生成文件名
      const fileName = this.generateFileName(file)
      const key = `${config.dir}/${fileName}`

      // 4. 构建表单数据
      const formData = new FormData()
      formData.append('name', fileName)
      formData.append('policy', config.policy)
      formData.append('OSSAccessKeyId', config.accessid)
      formData.append('success_action_status', '200')
      formData.append('signature', config.signature)
      formData.append('key', key)
      formData.append('file', file)

      // 5. 上传到 OSS
      await this.uploadToOSS(config.host, formData, onProgress)

      // 6. 返回结果
      return {
        key,
        url: `${config.host}/${key}`
      }
    } catch (error) {
      console.error('[UploadService] 上传失败:', error)
      throw error
    }
  }

  /**
   * 批量上传
   */
  async uploadMultiple(
    files: File[],
    options?: {
      scene?: string
      keyType?: number
      onProgress?: (index: number, progress: number) => void
      onComplete?: (index: number, result: UploadResult) => void
      onError?: (index: number, error: Error) => void
    }
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = []

    for (let i = 0; i < files.length; i++) {
      try {
        const result = await this.upload({
          file: files[i],
          scene: options?.scene,
          keyType: options?.keyType,
          onProgress: (progress) => options?.onProgress?.(i, progress)
        })
        results.push(result)
        options?.onComplete?.(i, result)
      } catch (error) {
        options?.onError?.(i, error as Error)
        // 继续上传其他文件
      }
    }

    return results
  }

  /**
   * 验证文件
   */
  async validateFile(file: File): Promise<void> {
    // 检查文件类型
    if (!this.limits.allowedTypes.includes(file.type as SupportedFileType)) {
      throw new Error(`不支持的文件类型: ${file.type}`)
    }

    // 检查文件大小
    if (file.size > this.limits.maxSize) {
      const maxSizeMB = this.limits.maxSize / 1024 / 1024
      throw new Error(`文件大小超过限制: ${maxSizeMB}MB`)
    }

    // 检查图片尺寸（如果是图片）
    if (file.type.startsWith('image/')) {
      await this.validateImageDimensions(file)
    }
  }

  /**
   * 验证图片尺寸
   */
  private validateImageDimensions(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)

      img.onload = () => {
        URL.revokeObjectURL(url)

        const { minWidth, maxWidth, minHeight, maxHeight } = this.limits

        if (minWidth && img.width < minWidth) {
          reject(new Error(`图片宽度不能小于 ${minWidth}px`))
          return
        }
        if (maxWidth && img.width > maxWidth) {
          reject(new Error(`图片宽度不能大于 ${maxWidth}px`))
          return
        }
        if (minHeight && img.height < minHeight) {
          reject(new Error(`图片高度不能小于 ${minHeight}px`))
          return
        }
        if (maxHeight && img.height > maxHeight) {
          reject(new Error(`图片高度不能大于 ${maxHeight}px`))
          return
        }

        resolve()
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('无法读取图片'))
      }

      img.src = url
    })
  }

  /**
   * 生成文件名
   */
  private generateFileName(file: File): string {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 10)
    const ext = file.name?.split('.').pop() || file.type.split('/')[1] || 'png'
    return `${timestamp}_${random}.${ext}`
  }

  /**
   * 上传到 OSS
   */
  private uploadToOSS(
    host: string,
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      // 进度监听
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100)
            onProgress(progress)
          }
        }
      }

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve()
        } else {
          reject(new Error(`上传失败: ${xhr.status}`))
        }
      }

      xhr.onerror = () => {
        reject(new Error('网络错误'))
      }

      xhr.open('POST', host)
      xhr.send(formData)
    })
  }

  /**
   * 获取上传限制配置
   */
  getLimits(): UploadLimits {
    return { ...this.limits }
  }

  /**
   * 更新上传限制配置
   */
  setLimits(limits: Partial<UploadLimits>): void {
    this.limits = { ...this.limits, ...limits }
  }
}
